import React, { useState, useEffect, useCallback } from "react";
import { sharingApi, recipesApi } from "../services/api";
import ShareRecipeModal from "../components/ShareRecipeModal";
import "./SharedPage.css";
import "./Page.css";

export default function SharedPage() {
  const [tab, setTab] = useState("received");
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [recv, snt] = await Promise.all([
        sharingApi.getReceived(),
        sharingApi.getSent(),
      ]);
      setReceived(recv);
      setSent(snt);
    } catch {
      setError("Failed to load shared recipes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleImport = async (id, dishName) => {
    try {
      const result = await sharingApi.importRecipe(id);
      setMessage(`✅ ${result.message}`);
      setTimeout(() => setMessage(null), 3000);
      fetchAll();
    } catch {
      setError("Failed to import recipe.");
    }
  };

  const handleDelete = async (id) => {
    try {
      await sharingApi.delete(id);
      fetchAll();
    } catch {
      setError("Failed to delete.");
    }
  };

  const handleShared = () => {
    setShowShareModal(false);
    setMessage("✅ Recipe shared successfully!");
    setTimeout(() => setMessage(null), 3000);
    fetchAll();
  };

  const parseIngredients = (ing) => {
    try { return JSON.parse(ing || "[]"); }
    catch { return []; }
  };

  const formatDate = (dt) => new Date(dt).toLocaleDateString("en-US", {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit"
  });

  const newCount = received.filter((r) => !r.is_imported).length;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">📬 Shared Recipes</h1>
          <p className="page-sub">Recipes shared between households</p>
        </div>
        <button className="btn-primary" onClick={() => setShowShareModal(true)}>
          📤 Share a Recipe
        </button>
      </div>

      {message && <div className="success-banner">{message}</div>}
      {error && <div className="error-banner" onClick={() => setError(null)}>{error} ✕</div>}

      {/* Tabs */}
      <div className="shared-tabs">
        <button
          className={`shared-tab ${tab === "received" ? "active" : ""}`}
          onClick={() => setTab("received")}
        >
          📥 Received
          {newCount > 0 && <span className="shared-badge">{newCount}</span>}
        </button>
        <button
          className={`shared-tab ${tab === "sent" ? "active" : ""}`}
          onClick={() => setTab("sent")}
        >
          📤 Sent
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading…</div>
      ) : tab === "received" ? (
        received.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <p>No recipes received yet.</p>
            <p style={{fontSize:"13px",marginTop:"8px",color:"var(--text-muted)"}}>
              Set your @username in ⚙️ Settings so others can share with you.
            </p>
          </div>
        ) : (
          <div className="shared-list">
            {received.map((item) => {
              const ingredients = parseIngredients(item.ingredients);
              return (
                <div key={item.id} className={`shared-card ${!item.is_imported ? "new" : ""}`}>
                  <div className="shared-card-header">
                    <div>
                      <div className="shared-dish-name">{item.dish_name}</div>
                      <div className="shared-from">
                        from {item.from_household_username
                          ? `@${item.from_household_username}`
                          : item.from_household_name || "Unknown"}
                        {" · "}{formatDate(item.shared_at)}
                      </div>
                    </div>
                    {!item.is_imported ? (
                      <span className="shared-new-badge">New</span>
                    ) : (
                      <span className="shared-imported-badge">✅ Imported</span>
                    )}
                  </div>

                  {ingredients.length > 0 && (
                    <div className="shared-ingredients">
                      <div className="shared-section-label">Ingredients</div>
                      <div className="shared-ing-list">
                        {ingredients.map((ing, i) => (
                          <span key={i} className="shared-ing-tag">{ing}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {item.youtube_link && (
                    <div className="shared-youtube">
                      <div className="shared-section-label">Recipe Video</div>
                      <a href={item.youtube_link} target="_blank" rel="noreferrer"
                        className="shared-yt-link">
                        ▶ Watch on YouTube
                      </a>
                    </div>
                  )}

                  {item.instructions && (
                    <div className="shared-instructions">
                      <div className="shared-section-label">Instructions</div>
                      <p className="shared-instructions-text">{item.instructions}</p>
                    </div>
                  )}

                  <div className="shared-card-actions">
                    {!item.is_imported && (
                      <button className="shared-import-btn"
                        onClick={() => handleImport(item.id, item.dish_name)}>
                        📖 Import to Recipe Book
                      </button>
                    )}
                    <button className="shared-delete-btn" onClick={() => handleDelete(item.id)}>
                      🗑 Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        sent.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📤</div>
            <p>No recipes sent yet.</p>
          </div>
        ) : (
          <div className="shared-list">
            {sent.map((item) => {
              const ingredients = parseIngredients(item.ingredients);
              return (
                <div key={item.id} className="shared-card">
                  <div className="shared-card-header">
                    <div>
                      <div className="shared-dish-name">{item.dish_name}</div>
                      <div className="shared-from">
                        to {item.from_household_username
                          ? `@${item.from_household_username}`
                          : item.from_household_name || "Unknown"}
                        {" · "}{formatDate(item.shared_at)}
                      </div>
                    </div>
                  </div>
                  {ingredients.length > 0 && (
                    <div className="shared-ingredients">
                      <div className="shared-section-label">Ingredients</div>
                      <div className="shared-ing-list">
                        {ingredients.map((ing, i) => (
                          <span key={i} className="shared-ing-tag">{ing}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}

      {showShareModal && (
        <ShareRecipeModal
          onShared={handleShared}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </div>
  );
}
